import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useVehicleGalleryContext } from '@/contexts/vehicle-gallery-provider';
import { toast } from '@/hooks/use-toast';
import { vehicleImageStore } from '@/models/stores/vehicle-images';
import { pointerIntersection } from '@dnd-kit/collision';
import { useSortable } from '@dnd-kit/react/sortable';
import clsx from 'clsx';
import { Check, RefreshCcw, Star, Trash2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { GalleryImageSize } from '../constants';
import { ImageUploadHelper } from '../helpers';
import type { VehicleImage } from '../types';
import { LoadingSpinner } from './loading-spinner';
import ProgressiveImage from './progressive-image';
import { VisuallyHiddenInput } from './visually-hidden-input';

export type DraggableImageProps = {
  image: VehicleImage;
  className?: string;
  disableMenu?: boolean;
  disableDragging?: boolean;
  isDragOverlay?: boolean;
};

/**
 * Responsible for all actions that can be performed on a single image.:
 *  - Mark as featured
 *  - Replace image
 *  - Delete image
 */
export const DraggableImage = ({
  image,
  disableMenu,
  disableDragging,
  isDragOverlay,
  className,
}: DraggableImageProps) => {
  const fileUploaderElRef = useRef<HTMLInputElement>(null);
  const [isReplacing, setIsReplacing] = useState(false);

  const {
    vehicleListingID,
    vehicleImages,
    setVehicleImages,
    showImageCategoryEditor,
    isImageSelected,
    toggleSelectedImageID,
  } = useVehicleGalleryContext();
  const { ref, isDragging } = useSortable({
    id: image.id,
    index: image.order_index,
    type: 'vehicle-image',
    accept: ['vehicle-image', 'placeholder'],
    disabled: disableDragging || isReplacing || showImageCategoryEditor,
    collisionDetector: pointerIntersection,
  });

  const onMarkFeaturedCb = useCallback(async () => {
    const oldImages = [...vehicleImages];

    const [updatedImages, request] =
      vehicleImageStore.markVehicleImageAsFeatured(vehicleListingID, image.id);
    try {
      setVehicleImages(updatedImages);
      await request;

      toast({
        title: 'Success',
        description: 'Marked image as featured!',
        className: 'bg-green-500',
      });
    } catch (error) {
      setVehicleImages(oldImages);
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to mark image as featured.',
        variant: 'destructive',
      });
    }
  }, [vehicleListingID, image, vehicleImages]);

  const onDeleteCb = useCallback(async () => {
    const oldImages = [...vehicleImages];
    const [updatedImages, request] = vehicleImageStore.deleteImage(
      vehicleListingID,
      image.id,
    );

    try {
      setVehicleImages(updatedImages);

      await request;

      toast({
        title: 'Success',
        description: 'Image deleted',
        className: 'bg-green-500',
      });
    } catch (error) {
      setVehicleImages(oldImages);
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to delete image.',
        variant: 'destructive',
      });
    }
  }, [vehicleListingID, image]);

  const onReplaceCb = useCallback(async () => {
    if (!fileUploaderElRef.current || !fileUploaderElRef.current.files) {
      return;
    }
    const imgCopy: VehicleImage = {
      ...image,
      file: fileUploaderElRef.current.files[0],
    };

    try {
      setIsReplacing(true);

      const updatedImg = await ImageUploadHelper.ReplaceImage(imgCopy);

      setVehicleImages((images) => {
        return images.map((img) => {
          if (img.id == updatedImg.id) {
            img.timestamp = updatedImg.timestamp;
          }

          return img;
        });
      });

      toast({
        title: 'Success',
        description: 'Image replaced',
        className: 'bg-green-500',
      });
    } catch (error) {
      console.log(error);
      toast({
        title: 'Error',
        description: 'Failed to replace an image.',
        variant: 'destructive',
      });
    } finally {
      if (fileUploaderElRef.current) {
        fileUploaderElRef.current.value = '';
      }
      setIsReplacing(false);
    }
  }, [vehicleListingID]);

  const removeImagePreparedForUploadCb = useCallback(() => {
    setVehicleImages((images) => {
      return images.filter((currentImg) => currentImg.id !== image.id);
    });
  }, []);

  const showSideMenu = !disableMenu && !isReplacing && !showImageCategoryEditor;

  return (
    <div
      ref={ref}
      style={GalleryImageSize}
      className={clsx(`relative overflow-hidden group ${className}`, {
        'grayscale opacity-70': !isDragOverlay && isDragging,
        'cursor-move': showSideMenu,
        'cursor-pointer': showImageCategoryEditor,
        "border-4 border-blue-600 before:content-['Upload'] before:absolute before:right-0 before:top-0 before:bg-blue-600 before:text-white before:py-1 before:px-2 before:rounded-bl-lg":
          image.type === 'upload_prepare',
        'border-4 border-blue-600': isImageSelected(image.id),
      })}
      onClick={() => {
        if (showImageCategoryEditor) {
          toggleSelectedImageID(image.id);
        }
      }}
    >
      {showSideMenu && (
        <div
          className={clsx(
            'img-menu-drawer absolute top-0 left-0 h-full w-1/5 flex flex-col items-center gap-2 p-2 bg-gray-500/90',
            'transition-transform delay-300 duration-150 ease-in-out transform -translate-x-14 group-hover:translate-x-0 ',
          )}
        >
          {!image.featured && image.type !== 'upload_prepare' && (
            <>
              <Tooltip disableHoverableContent>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    className="cursor-pointer w-8"
                    onClick={onMarkFeaturedCb}
                  >
                    <Star />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark as featured</TooltipContent>
              </Tooltip>

              <Tooltip disableHoverableContent>
                <TooltipTrigger asChild>
                  <Button
                    variant="destructive"
                    className="cursor-pointer w-8"
                    onClick={onDeleteCb}
                  >
                    <Trash2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </>
          )}

          {image.type !== 'upload_prepare' && (
            <>
              <Tooltip disableHoverableContent>
                <TooltipTrigger asChild>
                  <Button
                    variant="warning"
                    type="button"
                    className="cursor-pointer w-8"
                    onClick={() => fileUploaderElRef.current?.click()}
                  >
                    <RefreshCcw />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Replace image</TooltipContent>
              </Tooltip>
              <VisuallyHiddenInput
                ref={fileUploaderElRef}
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                onChange={onReplaceCb}
              />
            </>
          )}

          {image.type === 'upload_prepare' && (
            <Button
              variant="destructive"
              type="button"
              className="cursor-pointer w-8"
              onClick={removeImagePreparedForUploadCb}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      )}

      {/*
        Upload preview images use base64 encoded format to show files before they are uploaded, but because of that adding
        query parameter (as we do below) would cause URL to be invalid.
       */}
      {image.type === 'upload_prepare' && (
        <img className="w-full h-full" src={image.image_url} />
      )}

      {image.type !== 'upload_prepare' && (
        <ProgressiveImage
          className="w-full h-full"
          /**
           * The timestamp is added as query parameter in order to force browser to re-load image in case new version of an image is uploaded.
           * This can happen in case image replace is performed.
           */
          src={`${image.image_url}?timestamp=${image.timestamp}`}
          blurHashURL={image.blur_hash_img}
        />
      )}

      {image.featured && (
        <div className="featured-img-marker absolute top-3 right-3">
          <Star fill="#FBBF23" />
        </div>
      )}
      {isReplacing && (
        <div className="flex items-center justify-center w-full h-full absolute left-0 top-0 bg-gray-500 opacity-80">
          <LoadingSpinner />
        </div>
      )}

      {isImageSelected(image.id) && (
        <div className="selected-img-marker absolute top-0 left-0 p-1 bg-blue-600 text-white">
          <Check />
        </div>
      )}

      {/* Images that need to be upload (preview) doesn't have category assigned yet */}
      {image.category && (
        <div className="absolute bottom-2 right-0 px-2 py-1 rounded-s-lg bg-white">
          {image.category.label}
        </div>
      )}
    </div>
  );
};
